import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Avatar,
  Tooltip,
  IconButton,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { PackageOrder, OrderStatus, PhoneCall } from '../types';
import { getSourceIcon, getSourceColor, getStatusInfo, getPaymentInfo, getWaitingColor, formatWaitingTime, TODAY } from '../utils/helpers';
import { CompactCell } from '../styles/StyledComponents';

// ============================================================================
// ORDERS TABLE COMPONENT - Sipariş Tablosu
// ============================================================================

export interface OrdersTableProps {
  orders: PackageOrder[];
  selectedOrderId: number | null;
  selectedOrderSource?: string | null;
  onSelectOrder: (orderId: number | null, source?: string) => void;
  customerPhoneFilter: string | null;
  statusFilter: OrderStatus | 'all';
  onStatusFilterClick: (event: React.MouseEvent<HTMLElement>) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, orderId: number, source?: string) => void;
  incomingCall: PhoneCall | null;
  themeColors: { primary: string; secondary: string };
  getUnmappedCount?: (order: PackageOrder) => number;
  onUnmappedBadgeClick?: (order: PackageOrder) => void;
  /**
   * PavoPay ile ödenip ödenmediğini döndürür.
   * Tamamlanmış PavoPay payment kayıtları üzerinden hesaplanır.
   */
  isPavoPaid?: (order: PackageOrder) => boolean;
}

// Kiosk sipariş numarasından kısa kodu çıkar
// Eski format: "D-20260213-103207-296D-2" → "D-2"
// Yeni format: "P-1" → "P-1"
const getKioskShortCode = (orderNo?: string): string => {
  if (!orderNo) return '';
  // Son D-sayı veya P-sayı pattern'ini bul
  const match = orderNo.match(/[DP]-\d+$/);
  return match ? match[0] : orderNo;
};

// Kiosk siparişi burada ye mi?
const isKioskDineIn = (orderNo?: string): boolean => {
  const code = getKioskShortCode(orderNo);
  return code.startsWith('D-');
};

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  selectedOrderId,
  selectedOrderSource,
  onSelectOrder,
  customerPhoneFilter,
  statusFilter,
  onStatusFilterClick,
  onMenuOpen,
  incomingCall,
  themeColors,
  getUnmappedCount,
  onUnmappedBadgeClick,
  isPavoPaid,
}) => {
  const { t } = useTranslation('salesSystem');
  return (
    <TableContainer 
      sx={{ flex: 1, m: 0.5, bgcolor: 'background.paper', borderRadius: 1, overflow: 'auto' }}
      onClick={() => onSelectOrder(null, undefined)}
    >
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <CompactCell sx={{ fontWeight: 'bold', width: 55, whiteSpace: 'nowrap' }}>{t('orderManagement.ordersTable.headers.no')}</CompactCell>
            {customerPhoneFilter && (
              <CompactCell sx={{ fontWeight: 'bold', width: 70 }}>{t('orderManagement.ordersTable.headers.date')}</CompactCell>
            )}
            <CompactCell sx={{ fontWeight: 'bold', width: 32 }}>{t('orderManagement.ordersTable.headers.source')}</CompactCell>
            <CompactCell sx={{ fontWeight: 'bold' }}>{t('orderManagement.ordersTable.headers.customer')}</CompactCell>
            <CompactCell sx={{ fontWeight: 'bold', width: 35, textAlign: 'center' }}>{t('orderManagement.ordersTable.headers.quantity')}</CompactCell>
            <CompactCell sx={{ fontWeight: 'bold', width: 42 }}>{t('orderManagement.ordersTable.headers.time')}</CompactCell>
            <CompactCell sx={{ fontWeight: 'bold', width: 55, textAlign: 'center' }}>{t('orderManagement.ordersTable.headers.waiting')}</CompactCell>
            <CompactCell sx={{ fontWeight: 'bold', width: 60, textAlign: 'center' }}>{t('orderManagement.ordersTable.headers.payment')}</CompactCell>
            <CompactCell sx={{ fontWeight: 'bold', width: 80, textAlign: 'right', whiteSpace: 'nowrap' }}>{t('orderManagement.ordersTable.headers.amount')}</CompactCell>
            <CompactCell sx={{ fontWeight: 'bold', width: 54, textAlign: 'center' }}>{t('orderManagement.ordersTable.headers.pavo')}</CompactCell>
            <CompactCell sx={{ fontWeight: 'bold', width: 70 }}>{t('orderManagement.ordersTable.headers.courier')}</CompactCell>
            {getUnmappedCount && (
              <CompactCell sx={{ fontWeight: 'bold', width: 44, textAlign: 'center' }}>{t('orderManagement.ordersTable.headers.category')}</CompactCell>
            )}
            <CompactCell sx={{ fontWeight: 'bold', width: 95, cursor: 'pointer' }} onClick={onStatusFilterClick}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {t('orderManagement.ordersTable.headers.status')}
                <FilterListIcon sx={{ fontSize: 14, color: statusFilter !== 'all' ? themeColors.primary : 'text.secondary' }} />
                {statusFilter !== 'all' && (
                  <Typography variant="caption" sx={{ fontSize: '0.6rem', color: themeColors.primary }}>
                    ({getStatusInfo(statusFilter).label})
                  </Typography>
                )}
              </Box>
            </CompactCell>
            <CompactCell sx={{ fontWeight: 'bold', width: 32, textAlign: 'center', display: 'none' }}>{t('orderManagement.ordersTable.headers.actions')}</CompactCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.length === 0 ? (
            <TableRow>
              <CompactCell colSpan={customerPhoneFilter ? (getUnmappedCount ? 14 : 13) : (getUnmappedCount ? 13 : 12)}>
                <Box 
                  display="flex" 
                  flexDirection="column" 
                  alignItems="center" 
                  justifyContent="center" 
                  py={4}
                >
                  <ShoppingCartIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    {t('orderManagement.ordersTable.empty.title')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('orderManagement.ordersTable.empty.subtitle')}
                  </Typography>
                </Box>
              </CompactCell>
            </TableRow>
          ) : (
            orders.map((order) => {
              const statusInfo = getStatusInfo(order.status);
              const paymentInfo = getPaymentInfo(order.paymentType);
              const isSelected = selectedOrderId === order.id && (!selectedOrderSource || selectedOrderSource === order.source);
              
              // Aktif aramayla ilişkili sipariş mi?
              const isActiveCallOrder = incomingCall && order.customerPhone === incomingCall.phone;
              const isActiveStatus = ['new', 'accepted', 'preparing', 'ready'].includes(order.status);
              const highlightOrder = isActiveCallOrder && isActiveStatus;
              
              return (
                <TableRow
                  key={`${order.source}_${order.id}`}
                  onClick={(e) => { e.stopPropagation(); onSelectOrder(order.id, order.source); }}
                  sx={{
                    cursor: 'pointer',
                    backgroundColor: isSelected 
                      ? '#1976d2 !important'
                      : highlightOrder 
                        ? '#fff3e0 !important'
                        : order.isPreOrder
                          ? '#fff8e1 !important'
                          : order.source === 'kiosk' && isKioskDineIn(order.shortOrderNo || order.externalOrderNo)
                            ? '#FFF3E0 !important'
                            : order.source === 'kiosk' && !isKioskDineIn(order.shortOrderNo || order.externalOrderNo)
                              ? '#E3F2FD !important'
                              : order.isPaid
                                ? '#e8f5e9 !important'
                                : order.isOnlinePayment && !order.isPaid
                                  ? '#e3f2fd !important'
                                  : 'inherit',
                    '&:hover': {
                      backgroundColor: isSelected 
                        ? '#1565c0 !important' // Daha koyu mavi
                        : highlightOrder
                          ? '#ffe0b2 !important'
                          : undefined,
                    },
                    borderLeft: isSelected 
                      ? '4px solid #0d47a1' 
                      : highlightOrder
                        ? '4px solid #ff9800'
                        : order.isPreOrder
                          ? '4px solid #ff6f00'
                          : '4px solid transparent',
                    transition: 'background-color 0.2s',
                    // Seçili satırdaki metinleri beyaz yap
                    '& td': isSelected ? { color: '#ffffff !important' } : {},
                    '& td .MuiTypography-root': isSelected ? { color: '#ffffff !important' } : {},
                    '& td .MuiAvatar-root': isSelected ? { border: '2px solid white' } : {},
                    animation: highlightOrder ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%': { opacity: 1 },
                      '50%': { opacity: 0.85 },
                      '100%': { opacity: 1 },
                    },
                  }}
                >
                  <CompactCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" fontWeight="bold" fontSize="0.85rem" noWrap>
                      {order.source === 'kiosk'
                        ? getKioskShortCode(order.shortOrderNo)
                        : ['qr', 'ecommerce'].includes(order.source) && order.shortOrderNo?.includes('-')
                          ? order.shortOrderNo.split('-').slice(-2).join('-')
                          : order.shortOrderNo}
                    </Typography>
                  </CompactCell>
                  {customerPhoneFilter && (
                    <CompactCell>
                      <Typography 
                        variant="caption" 
                        fontSize="0.75rem"
                        fontWeight={order.orderDate === TODAY ? 'bold' : 'normal'}
                        sx={{ 
                          color: order.orderDate === TODAY ? 'success.main' : 'text.secondary',
                        }}
                      >
                        {order.orderDate === TODAY ? t('orderManagement.ordersTable.today') : order.orderDate.slice(5).replace('-', '/')}
                      </Typography>
                    </CompactCell>
                  )}
                  <CompactCell>
                    {order.source !== 'kiosk' ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        <Tooltip title={order.source.charAt(0).toUpperCase() + order.source.slice(1)}>
                          <Avatar 
                            sx={{ 
                              width: 22, 
                              height: 22, 
                              bgcolor: getSourceColor(order.source),
                            }}
                          >
                            {getSourceIcon(order.source)}
                          </Avatar>
                        </Tooltip>
                        {order.source === 'getir' && order.deliveryType && (
                          <Tooltip title={order.deliveryTypeName || (order.deliveryType === 1 ? t('orderManagement.ordersTable.tooltips.getirGetirsin') : t('orderManagement.ordersTable.tooltips.restaurantBrings'))}>
                            <Chip
                              label={order.deliveryType === 1 ? 'GG' : 'RG'}
                              size="small"
                              sx={{
                                height: 18,
                                minWidth: 26,
                                fontSize: '0.65rem',
                                fontWeight: 'bold',
                                bgcolor: order.deliveryType === 1 ? '#5D3EBC' : '#E65100',
                                color: 'white',
                                '& .MuiChip-label': { px: 0.4 },
                              }}
                            />
                          </Tooltip>
                        )}
                        {order.isPreOrder && (
                          <Tooltip title={order.expectedDeliveryTime ? t('orderManagement.ordersTable.tooltips.preOrderTime', { time: (() => {
                            const utcStr = order.expectedDeliveryTime;
                            const utcDate = utcStr.endsWith('Z') ? new Date(utcStr) : new Date(utcStr + 'Z');
                            const trMs = utcDate.getTime() + 3 * 60 * 60 * 1000;
                            const trDate = new Date(trMs);
                            return String(trDate.getUTCHours()).padStart(2,'0') + ':' + String(trDate.getUTCMinutes()).padStart(2,'0');
                          })() }) : t('orderManagement.ordersTable.tooltips.preOrder')}>
                            <Chip
                              label="⏰"
                              size="small"
                              sx={{
                                height: 18,
                                minWidth: 22,
                                fontSize: '0.7rem',
                                fontWeight: 'bold',
                                bgcolor: '#ff6f00',
                                color: 'white',
                                '& .MuiChip-label': { px: 0.3 },
                                animation: 'preorderBlink 2s ease-in-out infinite',
                                '@keyframes preorderBlink': {
                                  '0%, 100%': { opacity: 1 },
                                  '50%': { opacity: 0.5 },
                                },
                              }}
                            />
                          </Tooltip>
                        )}
                        
                      </Box>
                    ) : null}
                  </CompactCell>
                  <CompactCell>
                    {order.source === 'kiosk' ? (
                      <Box>
                        <Typography variant="body2" fontWeight="bold" fontSize="0.85rem" noWrap>
                          {t('orderManagement.ordersTable.kioskCustomer')}
                        </Typography>
                        <Typography variant="caption" fontSize="0.75rem" fontWeight="bold" noWrap sx={{
                          color: isKioskDineIn(order.shortOrderNo || order.externalOrderNo) ? '#E65100' : '#1565C0',
                        }}>
                          {isKioskDineIn(order.shortOrderNo || order.externalOrderNo) ? t('orderManagement.ordersTable.selfService') : t('orderManagement.ordersTable.package')}
                        </Typography>
                      </Box>
                    ) : ['qr', 'ecommerce'].includes(order.source) ? (
                      <Box>
                        <Typography variant="body2" fontWeight="bold" fontSize="0.85rem" noWrap color="info.main">
                          🪑 {order.customerAddress || order.customerName || t('orderManagement.ordersTable.unknownTable')}
                        </Typography>
                        {order.customerName && order.customerName !== order.customerAddress && (
                          <Typography variant="caption" fontSize="0.75rem" color="text.secondary" noWrap>
                            {order.customerName}
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" fontWeight="medium" fontSize="0.85rem" noWrap>
                        {order.customerName}
                      </Typography>
                    )}
                  </CompactCell>
                  <CompactCell sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" fontSize="0.85rem">
                      {order.itemCount}
                    </Typography>
                  </CompactCell>
                  <CompactCell>
                    <Typography variant="body2" fontSize="0.8rem">{order.orderTime}</Typography>
                  </CompactCell>
                  <CompactCell sx={{ textAlign: 'center' }}>
                    <Typography 
                      variant="body2" 
                      fontSize="0.8rem" 
                      fontWeight="bold"
                      sx={{ color: getWaitingColor(order.waitingMinutes) }}
                    >
                      {formatWaitingTime(order.waitingMinutes)}
                    </Typography>
                  </CompactCell>
                  <CompactCell sx={{ textAlign: 'center' }}>
                    {order.source === 'kiosk' ? (
                      <Tooltip title={t('orderManagement.ordersTable.tooltips.kiosk')}>
                        <Typography variant="body2" fontSize="0.85rem">🖥️</Typography>
                      </Tooltip>
                    ) : (
                      <Tooltip title={paymentInfo.label}>
                        <Typography variant="body2" fontSize="0.85rem">
                          {paymentInfo.emoji}
                        </Typography>
                      </Tooltip>
                    )}
                  </CompactCell>
                  <CompactCell sx={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight="bold" fontSize="0.85rem" noWrap>
                        {order.totalAmount.toFixed(2)} ₺
                      </Typography>
                      {(order.isPaid || order.source === 'kiosk') ? (
                        <Tooltip title={
                          order.source === 'kiosk' 
                            ? t('orderManagement.ordersTable.tooltips.kioskPaid') 
                            : t('orderManagement.ordersTable.tooltips.paid', { method: order.paymentMethod === 'CASH' ? t('orderManagement.ordersTable.paymentMethods.cash') : order.paymentMethod === 'POS' ? t('orderManagement.ordersTable.paymentMethods.pos') : order.paymentMethod === 'PAVOPAY' ? t('orderManagement.ordersTable.paymentMethods.pavopay') : order.paymentMethod || t('orderManagement.ordersTable.paymentMethods.default') })
                        }>
                          <Chip 
                            label={order.paymentMethod === 'PAVOPAY' ? 'PAVO' : '✓'}
                            size="small" 
                            sx={{ 
                              height: 18, 
                              minWidth: 22, 
                              fontSize: '0.65rem',
                              fontWeight: 'bold',
                              bgcolor: order.paymentMethod === 'PAVOPAY' ? '#7c4dff' : undefined,
                              color: order.paymentMethod === 'PAVOPAY' ? '#fff' : undefined,
                              ...(!order.paymentMethod || order.paymentMethod !== 'PAVOPAY' ? {} : {}),
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                            color={order.paymentMethod === 'PAVOPAY' ? undefined : 'success'}
                          />
                        </Tooltip>
                      ) : order.isOnlinePayment ? (
                        <Tooltip title={t('orderManagement.ordersTable.tooltips.onlinePending')}>
                          <Chip 
                            label="⏳" 
                            size="small" 
                            color="info"
                            variant="outlined"
                            sx={{ 
                              height: 18, 
                              minWidth: 22, 
                              fontSize: '0.7rem',
                              '& .MuiChip-label': { px: 0.5 }
                            }} 
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title={t('orderManagement.ordersTable.tooltips.paymentPending')}>
                          <Chip 
                            label="₺" 
                            size="small" 
                            color="warning"
                            variant="outlined"
                            sx={{ 
                              height: 18, 
                              minWidth: 22, 
                              fontSize: '0.75rem',
                              fontWeight: 'bold',
                              '& .MuiChip-label': { px: 0.5 }
                            }} 
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </CompactCell>
                  <CompactCell sx={{ textAlign: 'center', p: '2px !important' }}>
                    {(() => {
                      const isPavo = (isPavoPaid?.(order) ?? false) || order.paymentMethod === 'PAVOPAY';
                      if (!isPavo) {
                        return (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>—</Typography>
                        );
                      }
                      return (
                        <Tooltip title={t('orderManagement.ordersTable.tooltips.pavoPaid')}>
                          <Chip
                            label="PAVO"
                            size="small"
                            sx={{
                              height: 18,
                              minWidth: 44,
                              fontSize: '0.62rem',
                              fontWeight: 'bold',
                              bgcolor: '#7c4dff',
                              color: '#fff',
                              '& .MuiChip-label': { px: 0.5 },
                            }}
                          />
                        </Tooltip>
                      );
                    })()}
                  </CompactCell>
                  <CompactCell>
                    <Typography 
                      variant="caption" 
                      fontSize="0.75rem" 
                      noWrap
                      sx={{
                        color: !order.courierName ? 'text.disabled' :
                               order.courierStatus === 'accepted' ? '#4CAF50' :
                               order.courierStatus === 'picked_up' ? '#9C27B0' :
                               order.courierStatus === 'delivered' ? '#2196F3' :
                               'text.primary',
                        fontWeight: order.courierStatus === 'accepted' || order.courierStatus === 'picked_up' ? 'bold' : 'normal',
                      }}
                    >
                      {order.courierName || '-'}
                    </Typography>
                  </CompactCell>
                  {getUnmappedCount && (() => {
                    const platformSources = ['trendyol', 'getir', 'migros', 'yemeksepeti'];
                    if (!platformSources.includes(order.source)) {
                      return <CompactCell sx={{ textAlign: 'center' }}><Typography variant="caption" color="text.disabled">—</Typography></CompactCell>;
                    }
                    const count = getUnmappedCount(order);
                    return (
                      <CompactCell sx={{ textAlign: 'center', p: '2px !important' }}>
                        {count > 0 ? (
                          <Tooltip title={t('orderManagement.ordersTable.tooltips.unmappedProducts', { count })}>
                            <Box
                              onClick={(e) => { e.stopPropagation(); onUnmappedBadgeClick?.(order); }}
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3,
                                bgcolor: '#ed6c02', color: '#fff', borderRadius: 1,
                                cursor: 'pointer', py: 0.5, px: 0.5, minHeight: 28,
                                '&:hover': { bgcolor: '#e65100' },
                                '&:active': { transform: 'scale(0.95)' },
                              }}
                            >
                              <WarningAmberIcon sx={{ fontSize: 16 }} />
                              <Typography variant="caption" fontWeight="bold" fontSize="0.75rem">{count}</Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('orderManagement.ordersTable.tooltips.allMapped')}>
                            <CheckCircleOutlineIcon sx={{ fontSize: 18, color: 'success.main' }} />
                          </Tooltip>
                        )}
                      </CompactCell>
                    );
                  })()}
                  <CompactCell>
                    <Chip
                      label={`${statusInfo.emoji} ${statusInfo.label}`}
                      size="small"
                      color={statusInfo.color}
                      sx={{ 
                        fontSize: '0.7rem', 
                        height: 22,
                        width: 110,
                        justifyContent: 'center',
                        '& .MuiChip-label': {
                          px: 0.5,
                          textAlign: 'center',
                          width: '100%',
                        }
                      }}
                    />
                  </CompactCell>
                  <CompactCell sx={{ textAlign: 'center', display: 'none' }}>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMenuOpen(e, order.id, order.source); }} sx={{ p: 0.25 }}>
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </CompactCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default OrdersTable;
